import { Component, OnInit, Input, ViewEncapsulation } from "@angular/core";
import { NzMessageService } from "ng-zorro-antd/message";
import { Observable } from "rxjs";
import { LineItemMonitorDataService } from "../line-item-monitor-data.service";
import {
  LineItemMonitorMeta,
  SuggestedLineItems,
  RevisionValues,
  UpdateLineItemParams,
} from "../line-item-monitor.model";
import { AppService } from "src/app/app.service";
import { Store } from "@ngrx/store";
import { AppState, stateGetter } from "src/app/store/state/app.state";
import { Dictionary } from "src/app/store/models/common_types";
import { CompanyPageMeta } from "src/app/store/models/company-page-store.model";
import { ActivatedRoute } from "@angular/router";
import {
  VAIconRegistryService,
  bell,
  check,
  exclamationTriangle,
  fileExcel,
} from "@va/core-library/icons";
import { faFileExcel, faBell } from "@fortawesome/pro-light-svg-icons";

@Component({
  selector: "app-line-item-monitor",
  templateUrl: "./line-item-monitor.component.html",
  host: { class: "line-item-monitor" },
  styleUrls: ["./line-item-monitor.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class LineItemMonitorComponent implements OnInit {
  @Input() ticker: string;
  @Input() lineItemsMeta: LineItemMonitorMeta;
  showRevisionSelectBox: boolean;
  revisionStart = "PreQ";
  revisionOptions = Object.keys(RevisionValues).map((value) => {
    return { label: RevisionValues[value], value: value };
  });
  isSaveDisabled: boolean;
  showEditModal = false;
  isInactiveCompany = true;
  isModelsOnlyUser = false;
  isSuggestedLILoaded = false;
  isLimEnabled = false;
  stdLineItems: Array<SuggestedLineItems> = [];
  editModalLoading: boolean;
  addColTableData;
  hasError = false;
  isNoLineSelected = false;
  errMsg: string;
  dataObservable: Observable<LineItemMonitorMeta>;
  analyticsFeatures = [
    "MONITOR_TREND",
    "MONITOR_DISPERSION",
    "MONITOR_REVISION",
  ];
  visibleAlerts = false;
  routeSub: any;
  faFileExcel = faFileExcel;
  faBell = faBell;

  constructor(
    private dataService: LineItemMonitorDataService,
    private store: Store<AppState>,
    private message: NzMessageService,
    private route: ActivatedRoute,
    private appService: AppService,
    private iconRegistry: VAIconRegistryService,
  ) {
    this.iconRegistry.registerIcons([
      check,
      bell,
      fileExcel,
      exclamationTriangle,
    ]);
  }

  /**
   * ngOnInit sets dataObservable to get meta info. - if meta is not passed by parent component
   * if meta is passed, then use it.
   * Reason for following this approach is :- company-page needs monitor meta info for earnings preview
   * section -- check Next Earnings section shown at top of company page for sellside user.
   * So company page loads monitor meta info. But later on if we decide to have separate page for LIM, then this info
   * might have to be loaded here.
   */
  ngOnInit() {
    this.showEditModal = false;
    if (this.lineItemsMeta) {
      this.dataObservable = new Observable((observer) => {
        observer.next(this.lineItemsMeta);
        observer.complete();
      });
    } else {
      this.dataObservable = this.dataService.getLineItemMeta(this.ticker);
    }
    this.store
      .select(stateGetter("companyPage", "meta"))
      .subscribe((data: Dictionary<CompanyPageMeta>) => {
        if (data[this.ticker]) {
          this.isInactiveCompany = data[this.ticker].status === "Inactive";
          this.isModelsOnlyUser =
            data[this.ticker].user_type === "MODELS_ONLY_INBOX";
        }
      });
    const queryParams = this.route.snapshot.queryParams;
    if (
      queryParams &&
      queryParams.isLidAlertOpen &&
      queryParams.isLidAlertOpen.toLowerCase() === "true"
    ) {
      this.visibleAlerts = true;
    }
  }

  /**
   * Called by app-ark-container after successful completion of dataObservable i.e. getLineItemMeta call
   * It is also called whenere grid's data is refreshed due to other actions like change in list of line items,
   * changes in selected period for any column.
   * @param response LineItemMonitorMeta - Object containing meta info for LIM
   */
  onGridDataResolved(response: LineItemMonitorMeta) {
    this.isNoLineSelected = false;
    if (!response || response.is_models_only || response.entitled === false) {
      this.hasError = true;
      this.isLimEnabled = false;
      if (response.is_models_only) {
        this.errMsg =
          "Data is not available because this is a models-only company. Click Prioritize to request coverage";
      } else {
        this.errMsg = "Data is not available";
      }
    } else {
      const rowHeaders = {};
      this.lineItemsMeta = response;
      this.isLimEnabled = this.lineItemsMeta.lim_enabled;
      if (this.lineItemsMeta.line_items.length) {
        this.isNoLineSelected = false;
        response.line_items.forEach((line, index) => {
          rowHeaders[`${line.dapparameterid}`] = {
            header:
              line.display_unit !== response.currency_value
                ? `${line.header} (${line.display_unit})`
                : line.header,
            rowIndex: index,
          };
        });
        this.lineItemsMeta.rowHeaders = rowHeaders;
      } else {
        this.isNoLineSelected = true;
        this.errMsg = "No lines selected";
      }
    }
  }

  /**
   * Called on click of tab, it sets the visiblity of baseline dropdown shown in Revision tab.
   * Also logs click action in analytics.
   * @param tabNumber 0/1/2
   */
  onTabChange(tabNumber) {
    if (tabNumber === 2) {
      this.showRevisionSelectBox = true;
    } else {
      this.showRevisionSelectBox = false;
    }
    this.appService.logUserAction(
      {},
      this.ticker,
      "NMV",
      this.analyticsFeatures[tabNumber],
      {
        company_id: this.lineItemsMeta.companyid,
        beginning_state_referrer: "COMPANY_PAGE_MONITOR",
      },
      "",
      "MONITOR_TABS",
    );
  }

  /**
   * When customize line item button is clicked, this method is called. Its main aim is to make an api call
   * to get line items that are then shown in the modal.
   * It also does analytics logging for click.
   */
  createModal() {
    const reqObj = {
      ticker: this.ticker,
      cut: "NMV",
    };
    this.editModalLoading = true;
    this.dataService.getStdLineItems(reqObj).subscribe((res) => {
      const stdLineItems = res.data.map((tab) => {
        const tabData = tab.tab_data.map((tData) => ({
          name: tData.name,
          sli_id: Number(tData.dpid),
          dapparameterid: Number(tData.dpid),
        }));
        return {
          tab_full_name: tab.tab_full_name,
          tab_short_name: tab.tab_short_name,
          tab_sli_data: tabData,
        };
      });
      this.stdLineItems = stdLineItems;
      this.addColTableData = this.lineItemsMeta.line_items.map((row) => {
        /**
         * 1. copy header data to name as remove-model-section need name key
         * 2. line-item-modal component uses sli_id, so items added from add-modal-section has sli_id
         * and existing line items from remove-modal-section(which uses addColTableData) have dapparameterid,
         * so to keep it consistent - adding sli_id here as well.
         */
        return { ...row, name: row.header, sli_id: row.dapparameterid };
      });
      this.editModalLoading = false;
      this.showEditModal = true;
      this.appService.logUserAction(
        {},
        this.ticker,
        "NMV",
        "MONITOR_CUSTOMIZE_LINES",
        {
          company_id: this.lineItemsMeta.companyid,
          beginning_state_referrer: "COMPANY_PAGE_MONITOR",
        },
        "",
        "MONITOR_CUSTOMIZE_LINES_MODAL",
      );
    });
  }

  /**
   * This method is called when close button of line-item-edit modal is clicked
   */
  closeEditModal() {
    this.showEditModal = false;
  }

  /**
   * Makes api call to save updated lines by maintaining there sequence.
   * It does not make api call, if there is no change
   * i.e. neither sequence of lines is changed, nor is there any addition or deletion.
   * It silently closes the modal.
   */
  saveLineItems(modalData) {
    let isChanged = true;
    const gridDataDapParamIds = this.lineItemsMeta.line_items.map(
        (lineItem) => lineItem.dapparameterid,
      ),
      modalDataDapParamIds = modalData.map((lineItem) => lineItem.sli_id);
    isChanged =
      gridDataDapParamIds.length !== modalDataDapParamIds.length ||
      !gridDataDapParamIds.every((dapId, index) => {
        return dapId === modalDataDapParamIds[index];
      });
    if (isChanged) {
      const payload: UpdateLineItemParams = {
        ticker: this.ticker,
        line_ids: modalDataDapParamIds,
      };
      this.dataService.updateLimLines(payload).subscribe(
        (response) => {
          if ((response as any).message === "Lines updated successfully") {
            this.dataService
              .getLineItemMeta(this.ticker)
              .subscribe((response: any) => {
                // oGridDataResolved will change lineItemsMeta object which will lead to reloading grid
                // as for child component input i.e. lineItemsMeta is changing.
                this.onGridDataResolved(response.data);
                this.closeEditModal();
                this.appService.logUserAction(
                  {},
                  this.ticker,
                  "NMV",
                  "MONITOR_SAVE_LINES",
                  {
                    company_id: this.lineItemsMeta.companyid,
                    beginning_state_referrer: "COMPANY_PAGE_MONITOR",
                  },
                  "",
                  "MONITOR_CUSTOMIZE_LINES_MODAL",
                );
              });
          } else {
            this.message.error("Something went wrong");
          }
        },
        () => this.message.error("Something went wrong"),
      );
    } else {
      this.closeEditModal();
    }
  }

  /**
   * Called by app-ark-container if dataObservable i.e. getLineItemMeta call results in error or failure.
   * @param response api response
   */
  onFailure(response) {
    if (response.status === "error") {
      this.hasError = true;
    }
  }
  onPeriodUpdation(lineItemsMeta) {
    this.lineItemsMeta = lineItemsMeta;
  }

  /**
   * Calls export api and logs the event as well.
   * export api returns file name and tracker Id, which is used for logging user action.
   * @param exportType 0 or 1 - identifier for API, it represents which option is selected for export
   * default value for baseline will be "PreQ"
   */
  exportToExcel(exportType: number) {
    const extra = {
      export_type: exportType,
      baseline: this.revisionStart,
      template_type: "line item monitor",
    };
    this.appService
      .exportToExcel("company", this.ticker, extra)
      .subscribe((res) => {
        if (res["status"] === "success") {
          const fileName = res["filename"];
          const trackerId = res["trackerid"];
          this.appService.logUserAction(
            {},
            this.ticker,
            "",
            exportType === 1
              ? "MONITOR_EXCEL_EXPORT_VALUES"
              : "MONITOR_EXCEL_EXPORT_FORMULAS",
            {
              PAGE: "company",
              ticker: this.ticker,
              beginning_state_referrer: "Company page",
              export_type: exportType,
              tracker_id: trackerId,
              file_name: fileName,
            },
            "",
            "MONITOR_EXPORT",
          );
        }
      });
  }
  closeAlert() {
    this.visibleAlerts = false;
  }
}
